import { useId, useState, type FormEvent } from "react";
import { authService } from "../../features";
import { Button, Input, Notice } from "../ui";
import { AuthCard } from "./AuthCard";
import { PasswordInput } from "./PasswordInput";
import { SocialSignIn } from "./SocialSignIn";

export type AuthMode = "login" | "signup";
type Mode = AuthMode | "reset";

type AuthFormProps = {
  initialMode?: AuthMode;
  /** An error to show up front, such as from a failed email link. */
  initialError?: string | null;
  onBack?: () => void;
};

const copy = {
  login: {
    title: "Welcome back",
    intro: "Log in to continue to your account.",
    submit: "Log in",
    busy: "Logging in…",
  },
  signup: {
    title: "Create your account",
    intro: "Enter your details to get started.",
    submit: "Create account",
    busy: "Creating your account…",
  },
  reset: {
    title: "Reset your password",
    intro: "Enter your email and we’ll send you a link to choose a new one.",
    submit: "Send reset link",
    busy: "Sending…",
  },
} as const;

const linkClass =
  "rounded-sm font-semibold text-hazumu-terracotta transition-colors hover:text-hazumu-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink";

export const AuthForm = ({ initialMode = "login", initialError, onBack }: AuthFormProps) => {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    tone: "error" | "success";
    text: string;
  } | null>(initialError ? { tone: "error", text: initialError } : null);
  const passwordId = useId();
  const confirmPasswordId = useId();

  const switchMode = (nextMode: Mode) => {
    setMode(nextMode);
    setConfirmPassword("");
    setMessage(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage(null);

    if (mode === "signup" && password !== confirmPassword) {
      setMessage({
        tone: "error",
        text: "Those passwords don’t match. Please try again.",
      });
      return;
    }

    setLoading(true);

    if (mode === "login") {
      const error = await authService.logIn(email, password);
      if (error) setMessage({ tone: "error", text: error });
      else setPassword("");
    } else if (mode === "signup") {
      const { error, confirmEmail } = await authService.signUp(email, password);
      if (error) setMessage({ tone: "error", text: error });
      else {
        setPassword("");
        setConfirmPassword("");
        if (confirmEmail)
          setMessage({
            tone: "success",
            text: `We’ve sent a confirmation link to ${email}. Open it to finish creating your account.`,
          });
      }
    } else {
      const error = await authService.sendPasswordReset(email);
      // The same reply whether or not the email has an account, so the form
      // doesn't reveal who uses Hazumu.
      setMessage(
        error
          ? { tone: "error", text: error }
          : {
              tone: "success",
              text: `If there’s an account for ${email}, we’ve sent it a link to reset your password. It may take a minute to arrive.`,
            },
      );
    }

    setLoading(false);
  };

  const text = copy[mode];

  return (
    <AuthCard
      title={text.title}
      intro={text.intro}
      before={
        onBack && (
          <Button variant="ghost" className="-ml-4 mb-2" onClick={onBack}>
            &larr; Back
          </Button>
        )
      }
    >
      {mode !== "reset" && (
        <div className="mb-7 grid grid-cols-2 rounded-xl bg-hazumu-blush p-1">
          {(["login", "signup"] as const).map((item) => (
            <button
              className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink ${mode === item ? "bg-hazumu-white text-hazumu-ink shadow-sm shadow-hazumu-clay/30" : "text-hazumu-muted hover:text-hazumu-ink"}`}
              key={item}
              onClick={() => switchMode(item)}
              type="button"
            >
              {item === "login" ? "Log in" : "Sign up"}
            </button>
          ))}
        </div>
      )}

      {mode !== "reset" && (
        <SocialSignIn
          disabled={loading}
          onError={(text) => setMessage({ tone: "error", text })}
        />
      )}

      <form className="space-y-5" onSubmit={handleSubmit}>
        <label className="block text-sm font-medium">
          Email
          <Input
            autoComplete="email"
            className="mt-2 font-normal"
            inputSize="lg"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            required
            type="email"
            value={email}
          />
        </label>

        {mode !== "reset" && (
          <div>
            <div className="flex items-baseline justify-between gap-3">
              <label className="text-sm font-medium" htmlFor={passwordId}>
                Password
              </label>
              {mode === "login" && (
                <button
                  className="rounded-sm text-xs font-medium text-hazumu-muted underline underline-offset-3 transition-colors hover:text-hazumu-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink"
                  onClick={() => switchMode("reset")}
                  type="button"
                >
                  Forgot password?
                </button>
              )}
            </div>
            <PasswordInput
              autoComplete={
                mode === "login" ? "current-password" : "new-password"
              }
              className="mt-2"
              id={passwordId}
              minLength={6}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={mode === "signup" ? "At least 6 characters" : undefined}
              required
              value={password}
            />
          </div>
        )}

        {mode === "signup" && (
          <div>
            <label className="text-sm font-medium" htmlFor={confirmPasswordId}>
              Confirm password
            </label>
            <PasswordInput
              autoComplete="new-password"
              className="mt-2"
              id={confirmPasswordId}
              minLength={6}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Type it again"
              required
              value={confirmPassword}
            />
          </div>
        )}

        {message && <Notice tone={message.tone}>{message.text}</Notice>}

        <div>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={loading}
            type="submit"
          >
            {loading ? text.busy : text.submit}
          </Button>

          {mode === "signup" && (
            <p className="mt-3 text-center text-xs text-hazumu-muted">
              By creating an account you agree to our{" "}
              <a
                className="rounded-sm underline underline-offset-3 transition-colors hover:text-hazumu-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink"
                href="/privacy"
                rel="noreferrer"
                target="_blank"
              >
                privacy policy
              </a>{" "}
              and confirm you’re 16 or over.
            </p>
          )}
        </div>
      </form>

      <p className="mt-7 text-center text-sm text-hazumu-muted">
        {mode === "reset" ? (
          <>
            Remembered it?{" "}
            <button
              className={linkClass}
              onClick={() => switchMode("login")}
              type="button"
            >
              Back to log in
            </button>
          </>
        ) : (
          <>
            {mode === "login" ? "New to Hazumu?" : "Already have an account?"}{" "}
            <button
              className={linkClass}
              onClick={() => switchMode(mode === "login" ? "signup" : "login")}
              type="button"
            >
              {mode === "login" ? "Sign up" : "Log in"}
            </button>
          </>
        )}
      </p>
    </AuthCard>
  );
};
